import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { KeyRound, Eye, EyeOff, Loader2, AlertCircle, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Prefissi pubblici + login: su queste pagine il gate non deve mai comparire.
const SKIP_PREFIXES = ['/login', '/catalog/', '/shop-catalog/', '/inviti/', '/affluenza/', '/inventari/', '/clienti-pubblico/', '/ordini-pubblico/', '/prebook-pubblico/'];

/**
 * Overlay a schermo intero che obbliga l'utente a scegliere una nuova password
 * dopo che il master ne ha reimpostata una temporanea (must_change_password).
 * Blocca l'app finché il cambio non va a buon fine.
 */
export default function ForcePasswordChangeGate() {
  const { user, changePassword, logout } = useAuth();
  const location = useLocation();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Non mostrare su login/pagine pubbliche o se l'utente non deve cambiare.
  if (SKIP_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p))) return null;
  if (!user || !user.must_change_password) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError('La nuova password deve avere almeno 8 caratteri.'); return; }
    if (next !== confirm) { setError('Le due password non coincidono.'); return; }
    if (next === current) { setError('La nuova password deve essere diversa da quella temporanea.'); return; }
    setSubmitting(true);
    const res = await changePassword(current, next);
    setSubmitting(false);
    if (!res.success) { setError(res.error || 'Cambio password non riuscito.'); return; }
    // Successo: il context azzera must_change_password → il gate sparisce.
  };

  return (
    <div className="fixed inset-0 z-[100] bg-gray-900/60 flex items-end md:items-center justify-center p-0 md:p-4">
      <div className="bg-white w-full md:max-w-md rounded-t-xl md:rounded-xl shadow-xl max-h-svh overflow-y-auto">
        <div className="px-5 pt-5 pb-3 flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-gray-900 flex items-center justify-center flex-shrink-0">
            <KeyRound className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-gray-900 truncate">Imposta una nuova password</h2>
            <p className="text-xs text-gray-500 truncate">Richiesto al primo accesso dopo il reset</p>
          </div>
        </div>

        <form onSubmit={submit} className="px-5 pb-5 space-y-3">
          <p className="text-sm text-gray-600">
            Per continuare scegli una password personale. Inserisci prima la
            password temporanea che ti è stata comunicata.
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Password temporanea</label>
            <input
              type={show ? 'text' : 'password'}
              value={current}
              onChange={e => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Nuova password</label>
            <div className="relative">
              <input
                type={show ? 'text' : 'password'}
                value={next}
                onChange={e => setNext(e.target.value)}
                autoComplete="new-password"
                required
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
              />
              <button
                type="button"
                onClick={() => setShow(s => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-600"
                title={show ? 'Nascondi' : 'Mostra'}
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Almeno 8 caratteri.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Conferma nuova password</label>
            <input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
              <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-700">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2.5 rounded-xl bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? 'Salvataggio…' : 'Salva e continua'}
          </button>

          <button
            type="button"
            onClick={logout}
            className="w-full py-2 rounded-xl text-gray-500 text-sm font-medium hover:bg-gray-50 flex items-center justify-center gap-2"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </form>
      </div>
    </div>
  );
}
